// src/data/immigrationGuides/ilrGuide.js

const ilrGuide = {
    slug: "ilr-guide",

    title: "Indefinite Leave to Remain (ILR) UK Guide",

    seo: {
        metaTitle:
            "Indefinite Leave to Remain (ILR) UK Guide",
        metaDescription:
            "UK Indefinite Leave to Remain (ILR): eligibility, qualifying routes, residence requirements, the Life in the UK Test, English requirements and applying.",
        keywords: [
            "ILR UK",
            "Indefinite Leave to Remain",
            "ILR requirements",
            "UK settlement",
            "ILR application process"
        ]
    },

    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "ILR Guide",
            url: "/ilr-guide"
        }
    ],

    introduction: `
[Indefinite Leave to Remain](https://www.gov.uk/indefinite-leave-to-remain) (ILR), also known as settlement, allows eligible people
to live in the UK without a time limit.

ILR is an important milestone for many people who plan to make the UK their
permanent home and may later apply for British citizenship.

The requirements for ILR depend on the immigration route a person is applying
under. Different routes have different qualifying periods, residence rules and
eligibility criteria.
`,

    sections: [
        {
            heading: "What is Indefinite Leave to Remain?",
            content: `
Indefinite Leave to Remain is a form of UK immigration permission that allows a
person to remain in the UK permanently, without being subject to the time
restrictions that apply to temporary visas.

People with ILR can generally live, work and study in the UK without immigration
time limits, subject to meeting the relevant conditions.

ILR is separate from British citizenship. A person can hold ILR without becoming
a British citizen.
`
        },

        {
            heading: "Who can apply for ILR?",
            content: `
There is no single ILR route. Eligibility depends on the immigration category
you currently hold.

Common settlement routes include:

- Skilled Worker visa
- Family visa routes
- Global Talent visa
- Innovator Founder visa
- Long residence route
- Other routes specified in the Immigration Rules

Applicants must meet the requirements of the route they are applying under.
`
        },

        {
            heading: "Qualifying residence period",
            content: `
The amount of time required before applying for ILR depends on the immigration
route.

Some routes require a qualifying period of 5 years, while other routes may have
different requirements.

Applicants should check the settlement requirements for their specific visa
category before applying.
`
        },

        {
            heading: "Continuous residence requirements",
            content: `
Many ILR routes require applicants to meet continuous residence requirements.

This normally involves:

- spending the required qualifying period in the UK
- staying within permitted absence limits
- maintaining valid immigration permission

The absence rules depend on the immigration route and the relevant Immigration
Rules at the time of application.
`
        },

        {
            heading: "Life in the UK Test requirement",
            content: `
Many ILR applicants must pass the [Life in the UK Test](https://www.gov.uk/life-in-the-uk-test).

The test checks knowledge of:

- British history
- government and law
- traditions and customs
- everyday life in the UK

Applicants should prepare using the official Life in the UK Handbook and approved
preparation materials.
`
        },

        {
            heading: "English language requirement",
            content: `
Many ILR applicants must prove their [knowledge of English](https://www.gov.uk/english-language) unless they qualify for
an exemption.

Depending on individual circumstances, English may be demonstrated through:

- an approved English language qualification
- an eligible degree taught in English
- another exemption listed in the Immigration Rules

Applicants should check the current requirements before applying.
`
        },

        {
            heading: "ILR application process",
            content: `
The general ILR application process includes:

1. Checking eligibility for your immigration route
2. Completing the online application
3. Paying the required application fee
4. Providing supporting evidence
5. Providing biometric information if required
6. Waiting for a Home Office decision

The exact process depends on the type of ILR application being submitted.
`
        },

        {
            heading: "Documents commonly required",
            content: `
The documents required depend on the immigration route.

Applicants may commonly need:

- current passport or travel document
- evidence of immigration status
- evidence relating to residence
- English language evidence where required
- Life in the UK Test reference
- route-specific supporting documents

Applicants should follow the document checklist provided during the application.
`
        },

        {
            heading: "After receiving ILR",
            content: `
After ILR is granted, a person can continue living in the UK without a time limit.

Many ILR holders later choose to apply for [British citizenship](https://www.gov.uk/apply-citizenship-indefinite-leave-to-remain) if they meet the
naturalisation requirements.

Immigration status is now increasingly managed through digital records known as
eVisas.
`
        }
    ],

    checklist: [
        "Check your immigration route eligibility",
        "Confirm you have completed the required qualifying period",
        "Review your travel history and absences",
        "Pass the Life in the UK Test if required",
        "Confirm English language requirements",
        "Prepare supporting documents",
        "Complete the online application"
    ],

    faqs: [
        {
            question: "What is ILR in the UK?",
            answer:
                "Indefinite Leave to Remain is UK settlement status that allows eligible people to live in the UK without a time limit."
        },

        {
            question: "How long does it take to qualify for ILR?",
            answer:
                "The qualifying period depends on the immigration route. Many routes require five years, but some routes have different requirements."
        },

        {
            question: "Do I need the Life in the UK Test for ILR?",
            answer:
                "Many ILR applicants need to pass the Life in the UK Test unless they are exempt."
        },

        {
            question: "Do I need English for ILR?",
            answer:
                "Many ILR applicants need to meet the English language requirement unless an exemption applies."
        },

        {
            question: "Is ILR the same as British citizenship?",
            answer:
                "No. ILR gives permanent immigration permission, while British citizenship gives nationality status."
        }
    ],

    relatedPages: [
        {
            title: "British Citizenship Guide",
            url: "/british-citizenship-guide"
        },
        {
            title: "Life in the UK Test Preparation",
            url: "/mock-exams"
        },
        {
            title: "English Language Requirement",
            url: "/english-requirement"
        },
        {
            title: "UKVCAS Appointment Guide",
            url: "/ukvcas-appointment"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Indefinite Leave to Remain",
            url: "https://www.gov.uk/indefinite-leave-to-remain"
        },
        {
            title: "GOV.UK - Life in the UK Test",
            url: "https://www.gov.uk/life-in-the-uk-test"
        },
        {
            title: "GOV.UK - English Language Requirement",
            url: "https://www.gov.uk/english-language"
        },
        {
            title: "GOV.UK - Immigration Rules",
            url: "https://www.gov.uk/guidance/immigration-rules"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-20"
    }
};

export default ilrGuide;